import { useDispatch, useSelector } from "react-redux";

import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import { adminApi } from "@/store/apis/adminApi";
import "@/store/apis/adminApi.enhanced";
import authReducer from "@/store/slices/authSlice";

/**
 * Store único de Redux. Sigue el mismo patrón que el proyecto móvil
 * (Arya app) para que el equipo tenga mental model consistente:
 *
 *  - RTK Query es la fuente única de estado servidor.
 *  - `authSlice` mantiene el estado local de sesión (tokens, admin, MFA)
 *    con persistencia manual en localStorage (sin redux-persist).
 *  - `configureStore` inline (no helper) para que el tipo de `dispatch`
 *    conserve el middleware de RTK Query.
 */
const rootReducer = combineReducers({
  [adminApi.reducerPath]: adminApi.reducer,
  auth: authReducer,
});

export const store = configureStore({
  reducer: rootReducer,
  devTools: import.meta.env.DEV,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(adminApi.middleware),
});

// Habilita refetchOnFocus / refetchOnReconnect si algún query lo pide.
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

// Hooks tipados. Todo el código usa estos, no los "raw" de react-redux.
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
